/** Definitions two or more packages read: a book's balance, total assets (the I1 left side), the wholesale books, a model ref's registry key. */
import { AFS_BOOKS, ASSET_BOOKS, type BankState, type BookId, type StaticItemId, type Usd } from "./cp1_domain";

const STATIC_ASSETS = ["premises", "goodwill", "intangibles_cdi", "boli", "dta", "other_assets"] as const satisfies readonly StaticItemId[];
const isAfs = (book: BookId): boolean => (AFS_BOOKS as readonly BookId[]).includes(book);

/** Wholesale funding books (06.1: brokered + FHLB + FFP + DW), the numerator of `kpis.wholesaleShare`. */
export const WHOLESALE_BOOKS = ["brokered", "fhlb", "ffp", "dw"] as const satisfies readonly BookId[];

/** Σ balanceUsd over a book's vintages (amortized cost / par), in vintage order. */
export function bookBalanceUsd(bank: Pick<BankState, "books">, book: BookId): number {
  let t = 0;
  for (const v of bank.books[book].vintages) t += v.balanceUsd;
  return t;
}

/** I1 left side: AFS at fair value, net of ACL, + statics; the swap overlay's net fair value counts here when ≥ 0. */
export function totalAssetsUsd(bank: BankState): Usd {
  let t = -bank.aclUsd;
  for (const book of ASSET_BOOKS) {
    const afs = isAfs(book);
    for (const v of bank.books[book].vintages) t += afs ? (v.fairValueUsd ?? v.balanceUsd) : v.balanceUsd;
  }
  for (const item of STATIC_ASSETS) t += bank.staticItemsUsd[item];
  return (t + Math.max(0, bank.swaps.reduce((s, x) => s + x.fairValueUsd, 0))) as Usd;
}

/** Registry key of a model ref (CP-2 ModelRegistry): `${id}@${major}`. */
export const registryKey = (ref: { readonly id: string; readonly version: string }): string => `${ref.id}@${ref.version.split(".")[0] ?? "0"}`;
